'use client';

import { Suspense } from 'react';
import Sidebar from './Sidebar';
import ShellBrandBar from './ShellBrandBar';
import AuthGuard from './auth/AuthGuard';
import PostHogTenantGroup from './PostHogTenantGroup';
import PostHogUserIdentify from './PostHogUserIdentify';
import ContentDock from './ui/ContentDock';
import OfflineGate from './offline/OfflineGate';
import { SidebarAutoCollapseProvider } from './SidebarAutoCollapse';
import { ShellSideDockProvider, ShellSideDockHost } from './ui/ShellSideDock';
import { PAGE_GUARD_ZONE_ATTR } from '../hooks/usePageInteractionGuard';
import { OfflineSyncProvider } from './offline/OfflineSyncProvider';
import { InboxTopicReporter } from './inbox/InboxCountBridge';

interface DashboardShellProps {
    initialCollapsed: boolean;
    children: React.ReactNode;
}

// The unified SessionProvider is mounted by (dashboard)/layout.tsx so it survives
// navigation between this shell and the settings / super-admin take-over shells
// (BUG-929) — this shell only renders the classic chrome under the auth guard.
export default function DashboardShell({ initialCollapsed, children }: DashboardShellProps) {
    return (
        <>
            <Suspense fallback={null}>
                <PostHogUserIdentify />
            </Suspense>
            <AuthGuard>
              {/* BL-2344: the phone app's offline sync, under the signed-in session so it
                  knows the tenant and user it syncs for. Inert outside the native app. */}
              <OfflineSyncProvider>
              {/* BL-1987: a page (the docked lot panel) may ask the rail to fold. */}
              <SidebarAutoCollapseProvider>
                <ShellSideDockProvider>
                <PostHogTenantGroup />
                {/* BL-2491: mints the live Inbox count's topic for the signed-in person
                    at the active house and reports it to InboxCountBridge. */}
                <InboxTopicReporter />
                {/* `bg-sidebar` is the token this line used to hard-code — one
                    definition, in globals.css. Naming the hex here is exactly
                    what made the comment wrong the day the value moved
                    (BL-1736: #124743 → the brand green), so it doesn't. */}
                {/* `h-dvh`, not `h-screen` — same reason the clerk console gives
                    for its own frame: `100vh` on a mobile browser is the LARGE
                    viewport, measured with the toolbars retracted. With them
                    showing (the state a page loads in) this frame is taller than
                    what is visible, and because it is `overflow-hidden` the
                    excess cannot be scrolled back into reach — it sits under
                    Safari's bottom bar. `dvh` tracks the toolbar instead. */}
                {/* No text color on this frame — it is an ancestor of the page
                    content. It carried the rail's `text-slate-50`, so every
                    page element that named no color of its own inherited
                    near-white and vanished on the white page (BUG-1720: the
                    Transactions count and Gross total). The rail and the phone
                    bar already set every text color they paint, so page content
                    now falls through to `body`'s `--ink-900` in globals.css:
                      #111827 on #ffffff ..... 17.74:1  text ✓ (needs 4.5) */}
                {/* BUG-1888: the area a docked lot panel's unsaved-edits guard
                    watches. Portaled menus and dialogs sit outside it. */}
                {/* BL-2902: on paper the shell lets go of the screen (no fixed height, no
                    clipping, no rail or brand bar), so a page prints whole, as it scrolls. */}
                <div className="flex h-dvh overflow-hidden bg-sidebar print:block print:h-auto print:overflow-visible print:bg-white" {...{ [PAGE_GUARD_ZONE_ATTR]: '' }}>
                    {/* BL-1439 — the rail/drawer tier is decided in CSS, not JS.
                        Tailwind's `md` is 768px by default, which IS the AC
                        boundary, so this is correct from the first byte at every
                        width. A `useMediaQuery` gate could not be: its server
                        snapshot is `false`, so a phone would paint the rail for
                        one frame and then snap. Below the tier the rail is
                        `display:none` and `MobileNavBar` puts the same nav behind
                        a hamburger. */}
                    <div className="hidden shrink-0 md:block print:hidden">
                        <Sidebar initialCollapsed={initialCollapsed} />
                    </div>
                    {/* BL-1905 / BUG-1864: `relative` so the ContentDock anchors
                        to this box — everything right of the rail, the docked
                        lot panel included — for the bulk bar and the toasts. They
                        centre on it and may overlay the panel, rather than
                        squeezing into the page column beside it. */}
                    <div className="relative flex min-w-0 flex-1 overflow-hidden print:block print:overflow-visible">
                      <ContentDock>
                        <div className="flex min-w-0 flex-1 flex-col overflow-hidden print:block print:overflow-visible">
                        {/* BL-2553: the brand bar shows on root screens only; a drill-down
                            starts with its own Back row. */}
                        <ShellBrandBar className="md:hidden print:hidden" />
                        {/* BL-1037: `scrollbar-gutter: stable` reserves the vertical
                            scrollbar's width whether or not it's showing, so a classic
                            (width-consuming) scrollbar can't shrink the content and
                            push a table into a horizontal scrollbar — and content no
                            longer shifts when the scrollbar appears. */}
                            {/* BL-2422: the page's <main>, greyed and inert in the
                                phone app while offline on a page that needs a
                                connection. The phone bar above stays live. */}
                            <OfflineGate
                                data-testid="page-main"
                                // BL-2233: the page's scroller, named for the headers
                                // that fold on its scroll (useCollapsingHeader 'page').
                                data-page-scroll
                                // `scrollbar-gutter: stable` permanently reserves a
                                // scrollbar-width strip on the RIGHT, so the page's
                                // 16px `p-4` becomes 16px left and ~30px right — the
                                // whole toolbar row stops short of the gutter and the
                                // search box loses that width. It earns its keep on
                                // desktop, where it stops the layout jumping sideways
                                // as content crosses a screenful; a phone list is past
                                // that threshold from the first paint, and mobile
                                // scrollbars are overlays that reserve nothing anyway.
                                // So it starts at `md`.
                                className="relative flex-1 overflow-y-auto bg-white p-4 md:p-8 md:[scrollbar-gutter:stable] print:overflow-visible print:p-0"
                            >
                                {children}
                            </OfflineGate>
                        </div>
                        {/* BL-1994: the docked lot panel's full-height rail, beside
                            the page column and outside the page's scroll. It sits
                            inside the dock's box (BUG-1864), so the bulk bar and
                            toasts layer over it where they need the room. */}
                        <ShellSideDockHost />
                      </ContentDock>
                    </div>
                </div>
                </ShellSideDockProvider>
              </SidebarAutoCollapseProvider>
              </OfflineSyncProvider>
            </AuthGuard>
        </>
    );
}
